import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const statusConfig = {
  requested: { label: 'Requested', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  approved: { label: 'Approved', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  paid: { label: 'Paid', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  active: { label: 'Active', color: '#ffd700', bg: 'rgba(255,215,0,0.12)' },
  completed: { label: 'Completed', color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  cancelled: { label: 'Cancelled', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
};

const depositConfig = {
  held: { label: 'Deposit Held', color: '#60a5fa' },
  released: { label: 'Deposit Released', color: '#34d399' },
  partial_hold: { label: 'Partial Hold', color: '#fbbf24' },
};

const MyRentalsScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) fetchBookings();
  }, [user]);

  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        ?.from('rental_bookings')
        ?.select(`*, vehicles(title, make, model, year, vehicle_media(*))`)
        ?.eq('customer_id', user?.id)
        ?.order('created_at', { ascending: false });
      if (error) {
        if (error?.message?.includes('AbortError') || error?.message?.includes('Lock broken')) {
          // Retry once after a short delay on lock conflict
          setTimeout(() => fetchBookings(), 1000);
          return;
        }
        setError(error?.message);
      } else {
        setBookings(data || []);
      }
    } catch (e) {
      if (e?.message?.includes('AbortError') || e?.message?.includes('Lock broken')) {
        setTimeout(() => fetchBookings(), 1000);
        return;
      }
      console.error(e);
    }
    finally { setLoading(false); }
  };

  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'requested', label: 'Pending' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  const filtered = activeTab === 'all' ? bookings : bookings?.filter(b => b?.booking_status === activeTab);

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/customer-booking-screen')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
              <Icon name="ArrowLeft" size={20} className="text-white" />
            </button>
            <h1 className="text-white font-bold text-lg">My Rentals</h1>
          </div>
          <button onClick={() => navigate('/car-hire-listing')} className="btn-primary text-sm py-2 px-4">
            Book a Car
          </button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {!user ? (
          <div className="text-center py-24">
            <div className="text-6xl mb-4">🔐</div>
            <h3 className="text-white font-bold text-xl mb-2">Sign in to view rentals</h3>
            <p className="text-white/40 text-sm mb-6">You need to be logged in to see your rental history</p>
            <button onClick={() => navigate('/customer-login-screen')} className="btn-primary">Sign In</button>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {tabs?.map(tab => (
                <button key={tab?.key} onClick={() => setActiveTab(tab?.key)}
                  className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                  style={{ background: activeTab === tab?.key ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.06)', color: activeTab === tab?.key ? '#ffd700' : 'rgba(255,255,255,0.5)', border: `1px solid ${activeTab === tab?.key ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
                  {tab?.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3]?.map(i => (
                  <div key={i} className="rounded-2xl p-5 animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <div className="flex gap-4">
                      <div className="w-20 h-16 rounded-xl bg-white/10" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-white/10 rounded w-2/3" />
                        <div className="h-3 bg-white/10 rounded w-1/2" />
                        <div className="h-3 bg-white/10 rounded w-1/3" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered?.length === 0 ? (
              <div className="text-center py-24">
                <div className="text-6xl mb-4">🚗</div>
                <h3 className="text-white font-bold text-xl mb-2">No rentals found</h3>
                <p className="text-white/40 text-sm mb-6">Start your first car hire today</p>
                <button onClick={() => navigate('/car-hire-listing')} className="btn-primary">Browse Cars</button>
              </div>
            ) : (
              <div className="space-y-4">
                {filtered?.map(booking => {
                  const status = statusConfig?.[booking?.booking_status] || statusConfig?.requested;
                  const deposit = depositConfig?.[booking?.deposit_status] || depositConfig?.held;
                  const breakdown = booking?.price_breakdown || {};
                  return (
                    <div key={booking?.id} className="rounded-2xl p-5 transition-all hover:bg-white/[0.06]" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-start gap-4">
                        <div className="w-20 h-16 rounded-xl bg-white/10 flex items-center justify-center text-3xl flex-shrink-0">🚗</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-white font-bold text-sm truncate">{booking?.vehicles?.title || 'Vehicle'}</h3>
                            <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: status?.bg, color: status?.color }}>{status?.label}</span>
                          </div>
                          <p className="text-white/40 text-xs mb-2">#{booking?.booking_number}</p>
                          <div className="flex flex-wrap gap-3 text-xs text-white/50">
                            <span className="flex items-center gap-1"><Icon name="Calendar" size={11} />{formatDate(booking?.start_at)} → {formatDate(booking?.end_at)}</span>
                            <span className="flex items-center gap-1"><Icon name="MapPin" size={11} />{booking?.pickup_location || 'TBD'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="text-white/40 text-xs">Total</div>
                            <div className="text-[#ffd700] font-black text-base">KES {breakdown?.total?.toLocaleString() || '—'}</div>
                          </div>
                          <div>
                            <div className="text-white/40 text-xs">Deposit</div>
                            <div className="text-sm font-semibold" style={{ color: deposit?.color }}>{deposit?.label}</div>
                          </div>
                        </div>
                        {booking?.booking_status === 'completed' && (
                          <button onClick={() => navigate(`/rating-submission-screen?bookingId=${booking?.id}`)} className="btn-secondary text-xs py-2 px-3">
                            Rate Vehicle
                          </button>
                        )}
                        {booking?.booking_status === 'active' && (
                          <span className="px-3 py-1.5 rounded-xl text-xs font-bold" style={{ background: 'rgba(255,215,0,0.12)', color: '#ffd700' }}>
                            🔑 Active Rental
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyRentalsScreen;
